import React from 'react';
import { X, Clock, User, Shield, UserCheck, CheckCircle, XCircle, PlayCircle, StopCircle, UserPlus } from 'lucide-react';
import dateFormatter from '../utils/formatters';

const ActionHistoryModal = ({ isOpen, onClose, history }) => {
    if (!isOpen) return null;

    const getActionIcon = (action) => {
        switch (action) {
            case 'created': return <PlayCircle className="w-5 h-5 text-blue-500" />;
            case 'assigned': return <UserPlus className="w-5 h-5 text-purple-500" />;
            case 'started': return <PlayCircle className="w-5 h-5 text-green-500" />;
            case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
            case 'cancelled': return <XCircle className="w-5 h-5 text-red-500" />;
            case 'closed': return <StopCircle className="w-5 h-5 text-gray-500" />;
            case 'application_accepted': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'application_rejected': return <XCircle className="w-5 h-5 text-red-500" />;
            default: return <Clock className="w-5 h-5 text-gray-400" />;
        }
    };

    const getActionLabel = (action) => {
        const labels = {
            created: 'Job Created',
            assigned: 'Caregiver Assigned',
            started: 'Work Started',
            completed: 'Job Completed',
            cancelled: 'Job Cancelled',
            closed: 'Job Closed',
            application_accepted: 'Application Accepted',
            application_rejected: 'Application Rejected'
        };
        return labels[action] || action;
    };

    const getPerformerIcon = (role) => {
        switch (role) {
            case 'admin': return <Shield className="w-4 h-4 text-purple-600" />;
            case 'client': return <User className="w-4 h-4 text-blue-600" />;
            default: return <UserCheck className="w-4 h-4 text-green-600" />;
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Action History
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Timeline */}
                <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                    {history && history.length > 0 ? (
                        <div className="space-y-4">
                            {history.map((item, index) => (
                                <div key={index} className="flex gap-4">
                                    {/* Icon */}
                                    <div className="flex-shrink-0">
                                        <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                            {getActionIcon(item.action)}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 pb-4 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                                    {getActionLabel(item.action)}
                                                </h3>
                                                {item.performedByCaregiver && (
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        Caregiver: {item.performedByCaregiver.name}
                                                    </p>
                                                )}
                                                {item.details && (
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {item.details}
                                                    </p>
                                                )}
                                                {item.performedBy && (
                                                    <div className="flex items-center gap-2 mt-2">
                                                        {getPerformerIcon(item.performedBy.role)}
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                                            {item.performedBy.name} ({item.performedBy.role})
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            {dateFormatter.formatDate(item.performedAt)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            No action history available
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActionHistoryModal;
